import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function LSTMSection() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [training, setTraining] = useState(false);
  const [populating, setPopulating] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}` };

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/ml/lstm/status`, { headers });
      if (res.ok) setStatus(await res.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const populateSequences = async () => {
    setPopulating(true);
    try {
      const res = await fetch(`${API_URL}/api/ml/lstm/populate-sequences`, {
        method: 'POST', headers
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.sequences_inserted} secuencias creadas de ${data.picks_processed} picks`);
      }
    } catch (e) {
      toast.error('Error populando secuencias');
    } finally {
      setPopulating(false);
    }
  };

  const trainModel = async () => {
    setTraining(true);
    try {
      const res = await fetch(`${API_URL}/api/ml/lstm/train?epochs=30`, {
        method: 'POST', headers
      });
      if (res.ok) {
        const data = await res.json();
        if (data.error) {
          toast.error(data.error);
        } else {
          toast.success(`LSTM entrenado: ${data.accuracy}% accuracy, ${data.samples} muestras`);
          fetchStatus();
        }
      }
    } catch (e) {
      toast.error('Error entrenando LSTM');
    } finally {
      setTraining(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-[#00E599] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="lstm-section">
      {/* Status Card */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-white">Modelo LSTM</h3>
          <Badge className={`${status?.model_loaded ? 'bg-[#00E599]/20 text-[#00E599]' : 'bg-zinc-700 text-zinc-400'} border-0 text-[10px]`}>
            {status?.model_loaded ? 'Entrenado' : 'Sin entrenar'}
          </Badge>
        </div>
        <p className="text-xs text-zinc-400 mb-3">
          Red neuronal recurrente que analiza los últimos 5 partidos de cada equipo como secuencia temporal.
          Detecta tendencias de forma, momentum y patrones que el modelo estadístico no captura.
        </p>
        <div className="flex items-center gap-2">
          <Badge className="bg-zinc-800 text-zinc-300 border-0 text-[10px]">
            PyTorch: {status?.available ? 'OK' : 'No disponible'}
          </Badge>
          <Badge className="bg-zinc-800 text-zinc-300 border-0 text-[10px]">
            Secuencia: 5 partidos
          </Badge>
          <Badge className="bg-zinc-800 text-zinc-300 border-0 text-[10px]">
            Peso: 25% ensemble
          </Badge>
        </div>
      </div>

      {/* Training Info */}
      {status?.info?.trained_at && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
          <h4 className="text-xs text-zinc-500 uppercase mb-2">Último Entrenamiento</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <span className="text-[10px] text-zinc-500">Accuracy</span>
              <p className="text-lg font-bold text-[#00E599]">{status.info.accuracy}%</p>
            </div>
            <div>
              <span className="text-[10px] text-zinc-500">Muestras</span>
              <p className="text-lg font-bold text-white">{status.info.samples}</p>
            </div>
            <div>
              <span className="text-[10px] text-zinc-500">Val Loss</span>
              <p className="text-lg font-bold text-white">{status.info.best_val_loss}</p>
            </div>
            <div>
              <span className="text-[10px] text-zinc-500">Epochs</span>
              <p className="text-lg font-bold text-white">{status.info.epochs}</p>
            </div>
          </div>
          <p className="text-[10px] text-zinc-600 mt-2">
            Entrenado: {new Date(status.info.trained_at).toLocaleString('es-ES')}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="space-y-2">
        <Button
          onClick={populateSequences}
          disabled={populating}
          variant="outline"
          className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          data-testid="populate-sequences-btn"
        >
          {populating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          1. Generar Secuencias desde Picks
        </Button>
        <Button
          onClick={trainModel}
          disabled={training}
          className="w-full bg-[#00E599] text-black hover:bg-[#00E599]/90"
          data-testid="train-lstm-btn"
        >
          {training ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          2. Entrenar Modelo LSTM
        </Button>
        <p className="text-[10px] text-zinc-600 text-center">
          Primero genera las secuencias, luego entrena. El modelo se integrará automáticamente al generar picks.
        </p>
      </div>

      {/* How it works */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <h4 className="text-xs text-zinc-500 uppercase mb-2">Cómo funciona</h4>
        <div className="space-y-2 text-xs text-zinc-400">
          <div className="flex gap-2">
            <span className="text-[#00E599] font-bold">1.</span>
            <span>Extrae los últimos 5 partidos de cada equipo (goles, xG, posesión, ELO, forma)</span>
          </div>
          <div className="flex gap-2">
            <span className="text-[#00E599] font-bold">2.</span>
            <span>LSTM procesa la secuencia temporal para detectar tendencias y momentum</span>
          </div>
          <div className="flex gap-2">
            <span className="text-[#00E599] font-bold">3.</span>
            <span>El resultado se combina con Poisson+ELO+XGBoost (25% peso LSTM, 75% estadístico)</span>
          </div>
          <div className="flex gap-2">
            <span className="text-[#00E599] font-bold">4.</span>
            <span>Si todos los modelos coinciden, la confianza sube. Si discrepan, baja.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
